
import * as _ from "lodash";
import * as React from "react";
import {translate} from "react-i18next";
import {DropdownItemProps, FlagNameValues} from "semantic-ui-react";
import {Column} from "../../components/BasicDataGrid";
import {
    IVirtualizedTableColumn,
    ISelectFieldValue,
    TData,
    TDataValue,
    ITextExportStatus
} from "../../components/Table/VirtualizedTableInterfaces";
import {ITranslatedComponent} from "../../redux/reducers/generalReducer";
import {CategoryTextDto, WorkflowStatusDtoType, RoleDto, OptionBasicDtoOptionType} from "../../services/Client";
import {IRowStatusProps, valueState2str} from "../../services/WorkflowUtils";
import {BaseOptionsTableUtils} from "./BaseOptionsTableUtils";
import IBaseOptionsRow from "./BaseOptionsRow";
import BaseOptionsTable from "./BaseOptionsTable";
import {formatDate, SYSTEM_USER} from "../../components/Table/dataHandler/Utils";
import HATEOASClient from "src/services/HATEOASClient";

export interface IBaseOptionsTableContainerProps extends ITranslatedComponent {
    /** Columns to display */
    columns: Column[];

    /** Option basic rows */
    rows: IBaseOptionsRow[];

    /** Selected Option basic rows */
    selectedRows: IBaseOptionsRow[];

    /** Whether or not the table is in 'edit' mode (default: false) */
    editable: boolean;

    /** Callback to report which rows are selected. */
    onRowSelectionChanged: (rows: IBaseOptionsRow[]) => any;

    /** Callback for update of a row */
    onUpdateOptionBasic: (oldRow: IBaseOptionsRow, newRow: IBaseOptionsRow, rowIndex: number) => any;

    /** userRoles of the current user */
    userRoles?: RoleDto[];

    /** Categories for standard options */
    standardCategories?: CategoryTextDto[] | undefined;

    /** Categories for non standard options */
    nonSerCategories?: CategoryTextDto[] | undefined;
    client?: HATEOASClient;
    optionTypes:string[];
    onTriggerClick: (data:string[]) => void;
    resourceLinks?: any;
}

/**
 * The BaseOptionsTableContainer displays a collection of OptionBasicDtos in a data
 * grid. The client controls which columns to show and whether or not the
 * data grid is in edit mode.
 * Whenever a row is modified, the client is notified and is responsible
 * for providing the component with updated optionBasics.
 */
export class BaseOptionsTableContainer extends React.Component<IBaseOptionsTableContainerProps> {
    private utils: BaseOptionsTableUtils;
    constructor(props: IBaseOptionsTableContainerProps) {
        super(props);
        this.utils = new BaseOptionsTableUtils();
    }
    onChange = (cellKey: string, values: IBaseOptionsRow[]) => {
        if (!this.props.editable) {
            // don't allow any changes when not in edit mode. the status
            // control can fire change events even when not in edit mode.
            return;
        }

        // find each changed value and report it as modified
        values.forEach((value: IBaseOptionsRow) => {
            const rowIndex = this.props.rows.findIndex( (rowVal: IBaseOptionsRow) => rowVal.id === value.id);
            if (rowIndex >= 0 && this.props.onUpdateOptionBasic) {
                const oldRow = this.props.rows[rowIndex];
                const newRow = Object.assign({}, oldRow, value);

                if (newRow.statusValueState !== oldRow.statusValueState) {
                    // update human readable status text
                    newRow.statusView = valueState2str(newRow.statusValueState, newRow.workflowList);
                }

                // the view will update the table's props with updated rows
                this.props.onUpdateOptionBasic(oldRow, newRow, rowIndex);
            }
        });
    }

    onRowSelectionChanged = (values: Array<object>): void => {
        if (this.props.onRowSelectionChanged) {
            this.props.onRowSelectionChanged(values as IBaseOptionsRow[]);
        }
    }

    /**
     * Creates the respective rows for the table
     */
    private getRows() {
        const {t} = this.props;
        const rows = this.props.columns.map((item) => {
            const derivateList = (item as any).derivates;
            const row: IVirtualizedTableColumn = {
                key: item.key,
                label: t("COLUMN_FACTORY:" + item.name.replace("Name_", "").toUpperCase()),
                rowType: this.utils.getRowTypeforBaseOption(item.key),
                options: {
                    cellWidth: this.utils.getColumnDefaultWidth(item.key),
                    showOnStart: true,
                    derivateList,
                    readOnly: this.utils.isRowReadOnlyBasicOptions(item.key, !this.props.editable),
                    inputWithPopup:true,
                    isSortable:this.utils.isRowSortable(item.key)
                    }
            };
            return row;
        });
        return rows;
    }

    private extractMultiFields(item: IBaseOptionsRow, propKey: keyof IBaseOptionsRow): {} {
        return item[propKey.toString()] && item[propKey.toString()].map((value: any) => {
            return {
                flag: value.flag as FlagNameValues,
                value: value.text,
            };
        });
    }

    /**
     * Creates the respective data for the given item
     */
    private buildDataForBaseOption(item: IBaseOptionsRow): TData {
        const statusView: IRowStatusProps = {
            statusValueState: item.statusValueState,
            statusValueStateEditable: item.statusValueStateEditable,
            statusValueStateResetable: item.statusValueStateResetable,
            statusValueStates: item.statusValueStates,
            statusView: item.statusView,
            workFlowName: item.workFlowName,
            workflowType: item.workflowType,
            resetRoles: [],
        };

        const exportStatus: ITextExportStatus = {
            textExportDate: item.optionBasicDto.textExportDate,
            textExportStatus: item.optionBasicDto.textExportStatus
        };

        const categoryType: ISelectFieldValue = {
            value: item.category,
            options: this.props.nonSerCategories
                ? this.props.nonSerCategories.map((categoryItem) => {
                    const categoryTypeItem: DropdownItemProps = {
                        value: categoryItem.categoryCode, key: categoryItem.categoryCode,
                                text: categoryItem.text.length > 0  ?
                                    categoryItem.text : categoryItem.categoryCode
                     };
                    return categoryTypeItem;
                })
                : [],
            categoryText: item.categoryText.text,
            pccdCategoryOverride: item.optionBasicDto.pccdCategoryOverride
        };

        const optionTypes = this.props.optionTypes;
        const optionType: ISelectFieldValue = {
            value: item.optionType,
            options: optionTypes.length > 0 ?optionTypes.map((option:string)=> {
                return {text:option,value:option};
            }) :[{text:item.optionType,value:item.optionType}],
            optionType: item.optionType,
           
        };
       
        return {
            id:item.id,
            optionCode: item.optionCode,
            statusView: statusView,
            optionType:  optionType,// this.props.t("GLOBAL:" + item.optionType) + " (" + item.optionType + ')',
            category: item.category,
            categoryText: this.props.nonSerCategories && this.props.nonSerCategories.length !== 0
                ? categoryType
                : item.categoryText.text.length > 0
                    ? item.categoryText.text
                    : item.category,
            references: item.references,
            partNumber: item.partNumber,
            comment: item.comment,
            shorttext: this.extractMultiFields(item, "shorttext"),
            longtext: this.extractMultiFields(item, "longtext"),
            shortPCCDtext: this.extractMultiFields(item, "shortPCCDtext"),
            longPCCDtext: this.extractMultiFields(item, "longPCCDtext"),
            pdiptext:item.pdiptext,
            intSequence: item.optionBasicDto.intSequence,
            pccdIntSequenceOverride: item.optionBasicDto.pccdIntSequenceOverride,
            exportStatus: exportStatus,
            pdipFlag: item.pdipFlag,
            pdipMFamilie: item.pdipMFamilie,
            pdipMFamilieBez: item.pdipMFamilieBez,
            pccdFlag: item.pccdFlag,
            rowClass: item.pdipFlag ? '' : 'table-invalid-pdip-row',
            changedBy: item.changedBy === SYSTEM_USER ? this.props.t("GLOBAL:SYSTEM_USER") : item.changedBy,
            changedTimestamp: item.changedTimestamp ? formatDate(item.changedTimestamp) : undefined
        };
    }

    /**
     * Creates the respective data for the given item
     */
    private buildDataForStandardOption(item: IBaseOptionsRow): TData {
        const statusView: IRowStatusProps = {
            statusValueState: item.statusValueState,
            statusValueStateEditable: item.statusValueStateEditable,
            statusValueStateResetable: item.statusValueStateResetable,
            statusValueStates: item.statusValueStates,
            statusView: item.statusView,
            workFlowName: item.workFlowName,
            workflowType: item.workflowType,
            resetRoles: []
        };

        const categoryType: ISelectFieldValue = {
            value: item.category,
            options: this.props.standardCategories
                ? this.props.standardCategories.map((categoryItem) => {
                    const categoryTypeItem: DropdownItemProps = {
                        value: categoryItem.categoryCode, key: categoryItem.categoryCode,
                                text: categoryItem.text.length > 0  ?
                                    categoryItem.text : categoryItem.categoryCode
                     };
                    return categoryTypeItem;
                })
                : [],
            categoryText: item.categoryText.text
        };

        const exportStatus: ITextExportStatus = {
            textExportDate: item.optionBasicDto.textExportDate,
            textExportStatus: item.optionBasicDto.textExportStatus
        };

        const optionTypes = this.props.optionTypes;
        const optionType: ISelectFieldValue = {
            value: item.optionType,
            options: optionTypes.length > 0 ?optionTypes.map((option:string)=> {
                return {text:option,value:option};
            }) :[{text:item.optionType,value:item.optionType}],
            optionType: item.optionType,
           
        };
        return {
            id:item.id,
            optionCode: item.optionCode,
            statusView: statusView,
            optionType: optionType,// this.props.t("GLOBAL:" + item.optionType) + " (" + item.optionType + ')',
            category: item.category,
            categoryText: this.props.standardCategories && this.props.standardCategories.length !== 0
                ? categoryType
                : item.categoryText.text.length > 0
                    ? item.categoryText.text
                    : item.category,
            references: item.references,
            partNumber: item.partNumber,
            comment: item.comment,
            shorttext: this.extractMultiFields(item, "shorttext"),
            longtext: this.extractMultiFields(item, "longtext"),
            shortPCCDtext: this.extractMultiFields(item, "shortPCCDtext"),
            longPCCDtext: this.extractMultiFields(item, "longPCCDtext"),
            pdiptext:item.pdiptext,
            intSequence: item.optionBasicDto.intSequence,
            exportStatus: exportStatus,
            pdipFlag: item.pdipFlag,
            pdipMFamilie: item.pdipMFamilie,
            pdipMFamilieBez: item.pdipMFamilieBez,
            pccdFlag: item.pccdFlag,
            rowClass: item.pdipFlag ? '' : 'table-invalid-pdip-row',
            changedBy: item.changedBy === SYSTEM_USER ? this.props.t("GLOBAL:SYSTEM_USER") : item.changedBy,
            changedTimestamp: item.changedTimestamp ? formatDate(item.changedTimestamp) : undefined
        };
    }

    prepareOnChangeData(key: string, data: TDataValue, optionRows: IBaseOptionsRow[], targetIndex: number) {
        let payload: any = _.set({}, "id", optionRows[targetIndex].id);
        if (key === "statusView") {
            key = "statusValueState";
            payload = _.set(payload, "statusValueState", (data as any).statusValueState);
        }
        else if (key === "statusValueState") {
            payload = _.set(payload, "statusValueState", (data as any).statusValueState);
        }
        else if (key === "optionType") {
            payload = _.set(payload, "optionType", (data as any).value);
        }
        else if (key === "categoryText") {
            payload = _.set(payload, "category", (data as any).value);
        }
        else if (key === "shorttext") {
            const firstTargetShorttextIndex = this.props.rows[targetIndex].shorttext.findIndex((item) => item.flag === (data as any)[0].flag);
            const secondTargetShorttextIndex = this.props.rows[targetIndex].shorttext.findIndex((item) => item.flag === (data as any)[1].flag);
            const targetPayload=_.clone(this.props.rows[targetIndex].shorttext);
            targetPayload[firstTargetShorttextIndex].text=(data as any)[0].value;
            targetPayload[secondTargetShorttextIndex].text=(data as any)[1].value;
            console.log(targetPayload);
            payload = _.set(payload, key, targetPayload);
        }
        else if (key === "longtext") {
            const firstTargetLongtextIndex = this.props.rows[targetIndex].longtext.findIndex((item) => item.flag === (data as any)[0].flag);
            const secondTargetLongtextIndex = this.props.rows[targetIndex].longtext.findIndex((item) => item.flag === (data as any)[1].flag);
            const targetPayload=_.clone(this.props.rows[targetIndex].longtext);
            targetPayload[firstTargetLongtextIndex].text=(data as any)[0].value;
            targetPayload[secondTargetLongtextIndex].text=(data as any)[1].value;
            payload = _.set(payload, key, targetPayload);
        }

        else {
            payload = _.set(payload, key, data);
        }
        this.onChange(key, [payload]);
    }

    builtData = (rows: IBaseOptionsRow[]): TData[] => {
        return rows.map(row => {
            if (row.optionType === OptionBasicDtoOptionType.SER) {
                return this.buildDataForStandardOption(row);
            } else {
                return this.buildDataForBaseOption(row);
            }
        });
    }

    render() {
        const rowData = this.builtData(this.props.rows);

        return (
            <BaseOptionsTable
                workFlowResetStateDisabled={(key, data, readOnly) => {
                    if(readOnly)
                    return true;
                    if (this.props.userRoles) {
                        const tmpData: any = data;
                        const priv = _.find(tmpData.statusView.statusValueStates, (state) =>
                        !!_.find(this.props.userRoles, (element: RoleDto) => element && element.name === state.role));
                        return !priv;
                    }
                    return readOnly;
                }}
                inEditMode={this.props.editable}
                onChangeSelect={(indexes) => this.onRowSelectionChanged(indexes.map((index) => this.props.rows[index]))}
                columnsData={this.getRows()}
                data={rowData}
                onChange={(key, data, index) => this.prepareOnChangeData(key, data, this.props.rows, index)}
                isCellReadOnly={(key,data,readOnly)=> {
                    if(key === 'optionType' && (data as any).statusView.statusView !== WorkflowStatusDtoType.MAINTENANCE) {
                        return true;
                    }
                    if(key === "categoryText") {
                        const categoryTextObj: ISelectFieldValue = (data as any).categoryText;
                        return categoryTextObj.pccdCategoryOverride === undefined || categoryTextObj.pccdCategoryOverride === false ? false : true;
                    }
                    if(key === "intSequence") {
                        return (data as any).pccdIntSequenceOverride === undefined || (data as any).pccdIntSequenceOverride === false ? false : true;
                    }
                    if(key==="comment"||key==="categoryText") {
                        return readOnly;
                    }
                    if ((data as any).statusView.statusView === WorkflowStatusDtoType.FREEZE) {
                        const tmpData: any = data;
                        const currentStep = _.find(tmpData.statusView.statusValueStates, (element) => element.id === tmpData.statusView.statusValueState);
                        const priv = _.find(this.props.userRoles, (element: RoleDto) => element && element.name === currentStep.role);
                        if(!priv)
                        return true;
                        return (key === "derivateStates"||key==="shorttext"||key==="longtext"||key==="footer"||key==="medialong"||key==="usp");
                    }

                    if((data as any).statusView.statusView !== WorkflowStatusDtoType.MAINTENANCE&&
                    ((key === "derivateStates"||key==="shorttext"||key==="longtext"||key==="footer"||key==="medialong"||key==="usp")))
                    return true;
                    if(this.props.userRoles) {
                        const tmpData: any = data;
                        const currentStep = _.find(tmpData.statusView.statusValueStates, (item) => item.id === tmpData.statusView.statusValueState);
                        const priv = _.find(this.props.userRoles, (item: RoleDto)=> item.name === currentStep.role);
                        return !priv;
                    }
                    return readOnly;
                }}
                onTriggerClick={(data:string[])=> this.props.onTriggerClick(data)}
                resourceLinks={this.props.resourceLinks}
            />
        );
    }
}

export default translate(["BASEOPTIONS", "COLUMN_FACTORY"])(BaseOptionsTableContainer);



// WEBPACK FOOTER //
// ./src/views/BaseOptionsView/BaseOptionsTableContainer.tsx